import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  MailOutline as InviteIcon,
  Visibility as ViewAsIcon,
  CheckCircle as SignedInIcon,
} from '@mui/icons-material';
import { openViewAsTab } from '../../utils/viewAsSession';

const shortDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Portal access for one technician (admin): sign-in status, "Invite to
 * portal" (email with a 7-day set-password link + sign-in link) and
 * "View as technician" (read-only portal in a new tab).
 *
 * Props:
 *   technician – { userId, firstName, lastName, email, isActive }
 *   status     – { lastLogin, lastInvitedAt } (may be undefined while loading)
 *   onInvited(userId, invitedAt)
 */
const TechnicianPortalAccess = ({ technician, status, onInvited }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  const [viewAsError, setViewAsError] = useState('');
  const [openingView, setOpeningView] = useState(false);

  const name = `${technician.firstName || ''} ${technician.lastName || ''}`.trim() || technician.email;
  const lastLogin = status?.lastLogin;
  const lastInvitedAt = status?.lastInvitedAt;

  const statusChip = () => {
    if (lastLogin) {
      return (
        <Chip size="small" color="success" variant="outlined" icon={<SignedInIcon />}
          label={`Portal: signed in ${shortDate(lastLogin)}`} />
      );
    }
    if (lastInvitedAt) {
      return <Chip size="small" color="info" variant="outlined" label={`Invited ${shortDate(lastInvitedAt)} · not signed in yet`} />;
    }
    return <Chip size="small" variant="outlined" label="Never signed in" />;
  };

  const openDialog = () => {
    setMessage('');
    setSent(null);
    setError('');
    setDialogOpen(true);
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/technicians/${technician.userId}/portal-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send the invitation');
      setSent(data);
      if (onInvited) onInvited(technician.userId, data.invitedAt);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleViewAs = async () => {
    setViewAsError('');
    setOpeningView(true);
    try {
      await openViewAsTab({
        apiBase,
        technicianUserId: technician.userId,
        authToken: localStorage.getItem('token'),
      });
    } catch (err) {
      setViewAsError(err.message);
    } finally {
      setOpeningView(false);
    }
  };

  const canAct = technician.isActive !== false && !!technician.userId;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
      {statusChip()}
      <Tooltip title={canAct ? 'Email a link to set a password and sign in' : 'Reactivate this technician first'}>
        <span>
          <Button size="small" startIcon={<InviteIcon />} onClick={openDialog} disabled={!canAct || !technician.email}>
            {lastInvitedAt || lastLogin ? 'Resend invite' : 'Invite to portal'}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Open their portal in a new tab, read-only">
        <span>
          <Button
            size="small"
            startIcon={openingView ? <CircularProgress size={14} /> : <ViewAsIcon />}
            onClick={handleViewAs}
            disabled={!canAct || openingView}
          >
            View as technician
          </Button>
        </span>
      </Tooltip>
      {viewAsError && (
        <Typography variant="caption" color="error">{viewAsError}</Typography>
      )}

      <Dialog open={dialogOpen} onClose={() => !sending && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite {name} to the technician portal</DialogTitle>
        <DialogContent dividers>
          {sent ? (
            <Alert severity="success">
              Invitation sent to <strong>{sent.recipient}</strong>. The set-password link works for 7 days.
            </Alert>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
              <Typography variant="body2" sx={{ mb: 2 }}>
                {name} will receive an email at <strong>{technician.email}</strong> with:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
                <li>a <strong>Set my password &amp; sign in</strong> button (works for 7 days) — for technicians who never had or forgot their password;</li>
                <li>a <strong>Sign in</strong> link — for those who already know it. Their current password keeps working until they set a new one.</li>
              </Typography>
              <TextField
                label="Personal message (optional)"
                multiline
                minRows={3}
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: 1000 }}
                placeholder="e.g. Your schedule for next week is in the portal."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={sending}>{sent ? 'Done' : 'Cancel'}</Button>
          {!sent && (
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <InviteIcon />}
            >
              {sending ? 'Sending…' : 'Send invitation'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicianPortalAccess;
